import { AppPageLayout } from '@/components/layout/app-page-layout';
import { GlobalTopBar } from '@/components/layout/global-top-bar';
import { Button } from '@/components/ui/button';
import {
    convertZyXml,
    enrichImageAttributesInSection,
    enrichShortAnswerResourcePayloads,
    sectionToMigratedProseMirrorJson,
} from '@/lib/zybooks-parser';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { Input } from '../ui/input';

export function ZyXmlConverter() {
    const [zyxmlInput, setZyxmlInput] = useState('');
    const [sectionJsonOutput, setSectionJsonOutput] = useState<string>('');
    const [proseMirrorJsonOutput, setProseMirrorJsonOutput] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isConverting, setIsConverting] = useState(false);
    const [bookName, setBookName] = useState('');
    const [bookChapter, setBookChapter] = useState('');
    const [subject, setSubject] = useState('');
    const [releaseId, setReleaseId] = useState('');

    const wrapSnippetIfNeeded = (input: string): string => {
        const trimmed = input.trim();
        if (trimmed.includes('<zyChapter') || trimmed.includes('<zychapter')) {
            return trimmed;
        }

        const standaloneTopLevelTags = [
            'zyExercise',
            'zyExample',
            'zyAside',
            'zyExplore',
            'zyFigure',
            'zyYoutubeVideo',
            'zyAnimator',
            'zyCode',
            'zyCommonError',
            'zyDE',
            'zyHTML',
            'zyTool',
            'zyTable',
            'zyQSetMultipleChoice',
            'zyQSetShortAnswer',
            'zyQSetMatch',
            'zyQSetTF',
            'zyQuestionMultipleChoice',
            'zyQuestionShortAnswer',
            'zyQuestionMatch',
            'zyQuestionTF',
            'unsupported',
            'unsupported-editable',
        ];

        const isStandaloneTag = standaloneTopLevelTags.some(tag => {
            const regex = new RegExp(`^<${tag}[\\s>]`, 'i');
            return regex.test(trimmed);
        });

        if (isStandaloneTag) {
            return `<zyChapter title="title" shorttitle="shorttitle" chapter="${bookChapter}">
                        <zySection title="title" shorttitle="shorttitle" id="00000000-0000-0000-0000-000000000000" zymath="true">
                            ${trimmed}
                        </zySection>
                    </zyChapter>`;
        }

        return `<zyChapter title="title" shorttitle="shorttitle" chapter="1">
                        <zySection title="title" shorttitle="shorttitle" id="00000000-0000-0000-0000-000000000000" zymath="true">
                            <zyHTML id="00000000-0000-0000-0000-000000000001" caption="">
                                ${trimmed}
                            </zyHTML>
                        </zySection>
                </zyChapter>`;
    };

    const handleConvert = async () => {
        if (!zyxmlInput.trim()) {
            setError('Please enter some zyXML content');
            return;
        }

        setError(null);
        setSectionJsonOutput('');
        setProseMirrorJsonOutput('');
        setIsConverting(true);

        try {
            const wrappedInput = wrapSnippetIfNeeded(zyxmlInput);
            const wrappedInputWithBrReplace = wrappedInput.replaceAll('<br>', '<br/>');
            const converted = await convertZyXml(wrappedInputWithBrReplace, 'json', {
                devBookName: bookName,
                subject,
                releaseId,
            });

            if (!converted) {
                setError('Failed to convert zyXML content. Check the console for details.');
                setIsConverting(false);
                return;
            }

            const section = converted.section as Record<string, unknown>;
            const trimmedBook = bookName.trim();
            const trimmedChapter = bookChapter.trim();
            const rawCrs = section.content_resources;
            const content_resources = Array.isArray(rawCrs)
                ? rawCrs.map((cr: Record<string, unknown>) => {
                      const next = { ...cr };
                      if (trimmedBook) next.devBookName = trimmedBook;
                      if (trimmedChapter) next.chapter_number = trimmedChapter;
                      return next;
                  })
                : rawCrs;
            const sectionForOutput = { ...section, content_resources };
            enrichImageAttributesInSection(sectionForOutput, { subject, releaseId });
            enrichShortAnswerResourcePayloads(sectionForOutput, { subject, releaseId });

            const migratedPm = await sectionToMigratedProseMirrorJson(sectionForOutput);
            setSectionJsonOutput(JSON.stringify(sectionForOutput, null, 2));
            setProseMirrorJsonOutput(JSON.stringify(migratedPm, null, 2));
        } catch (err) {
            console.error('Conversion error:', err);
            setError(`Conversion error: ${err instanceof Error ? err.message : 'Unknown error'}`);
        } finally {
            setIsConverting(false);
        }
    };

    return (
        <AppPageLayout className="flex h-[calc(100svh-49px)] flex-col min-h-0 min-w-0">
            <GlobalTopBar.Start>
                <h1 className="text-lg font-semibold">zyXML converter</h1>
            </GlobalTopBar.Start>
            <AppPageLayout.Body className="min-h-0 flex-1 overflow-hidden">
                <div className="flex min-h-0 flex-1 gap-4 overflow-hidden p-4">
                    <div className="flex flex-1 flex-col gap-2">
                        <Input
                            placeholder="Book Name"
                            value={bookName}
                            onChange={e => setBookName(e.target.value)}
                        />
                        <Input
                            placeholder="Chapter Number"
                            value={bookChapter}
                            onChange={e => setBookChapter(e.target.value)}
                        />
                        <Input
                            placeholder="Subject"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                        />
                        <Input
                            placeholder="Release ID"
                            value={releaseId}
                            onChange={e => setReleaseId(e.target.value)}
                        />
                        <textarea
                            className="flex-1 resize-none rounded-md border p-3 font-mono text-sm"
                            placeholder={`Paste zyXML here...`}
                            value={zyxmlInput}
                            onChange={e => setZyxmlInput(e.target.value)}
                        />
                        <Button
                            onClick={handleConvert}
                            disabled={isConverting}
                        >
                            {isConverting ? (
                                <>
                                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                                    Converting...
                                </>
                            ) : (
                                'Convert to JSON'
                            )}
                        </Button>
                    </div>

                    <div className="flex flex-1 flex-col gap-3 overflow-hidden">
                        <div className="flex min-h-0 flex-1 flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">zyAuthor processor response (zyXml_to_json)</label>
                                {sectionJsonOutput && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigator.clipboard.writeText(sectionJsonOutput)}
                                    >
                                        Copy section JSON
                                    </Button>
                                )}
                            </div>
                            {error ? (
                                <div className="flex-1 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>
                            ) : (
                                <textarea
                                    className="min-h-[120px] flex-1 resize-none rounded-md border bg-gray-50 p-3 font-mono text-sm"
                                    value={sectionJsonOutput}
                                    readOnly
                                    placeholder="Parsed section JSON appears here (use for sandbox static files)."
                                />
                            )}
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium">
                                    ProseMirror JSON (zybookSectionToProseMirrorJson -&gt; migrateZybookMathStrings)
                                </label>
                                {proseMirrorJsonOutput && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => navigator.clipboard.writeText(proseMirrorJsonOutput)}
                                    >
                                        Copy ProseMirror JSON
                                    </Button>
                                )}
                            </div>
                            <textarea
                                className="min-h-[120px] flex-1 resize-none rounded-md border bg-gray-50 p-3 font-mono text-sm"
                                value={proseMirrorJsonOutput}
                                readOnly
                                placeholder="ProseMirror document JSON (for debugging)."
                            />
                        </div>
                    </div>
                </div>
            </AppPageLayout.Body>
        </AppPageLayout>
    );
}
